/** Data Tier 2 — client cloud storage (Setup metadata + Google Drive live path). */

export const CLOUD_PROVIDERS = [
  { value: 'pending', label: 'Connect later' },
  { value: 'google_drive', label: 'Google Drive' },
  { value: 'google_workspace', label: 'Google Workspace' },
  { value: 'onedrive', label: 'OneDrive' },
  { value: 'icloud_drive', label: 'iCloud Drive' },
  { value: 'dropbox', label: 'Dropbox' },
] as const;

export type CloudProviderValue = (typeof CLOUD_PROVIDERS)[number]['value'];

export const CLOUD_FOLDER_PLACEHOLDER = 'e.g. Mentology Mentor Working Files';

/** Organisation-shared policies / learning pack (AMD-T2 C2). */
export const ORG_SHARED_FOLDER_PLACEHOLDER = 'e.g. Mentology Org Policies & Learning Pack';

export const CLOUD_STORAGE_HELPER_LINE =
  'Web search uses a server-side API key only — no client device permissions.';

export const CLOUD_OAUTH_STUB_NOTE =
  'Google Drive and Google Workspace support live OAuth. OneDrive, Dropbox, and iCloud remain intent-only stubs until those apps are registered. Secrets stay in server environment variables.';

export const CLOUD_STORAGE_NOTE =
  "Tier 2 — business files remain in the client's own Drive/OneDrive; never duplicated into Mentology systems.";

export const LIVE_CLOUD_PROVIDERS: CloudProviderValue[] = ['google_drive', 'google_workspace'];

export function isGoogleCloudProvider(provider: unknown): boolean {
  return provider === 'google_drive' || provider === 'google_workspace';
}

export function isLiveCloudProvider(provider: unknown): boolean {
  return isGoogleCloudProvider(provider);
}

export function isStubCloudProvider(provider: unknown): boolean {
  return provider === 'onedrive' || provider === 'dropbox' || provider === 'icloud_drive';
}

export interface CloudStorageMeta {
  provider: CloudProviderValue;
  folder_label: string;
  folder_id?: string | null;
  connected: boolean;
  note: string;
}

export interface Tier2SelectedFile {
  drive_file_id: string;
  filename: string;
  category?: string;
  mime_type?: string | null;
  selected_at?: string;
}

export function normalizeCloudProvider(value: unknown): CloudProviderValue {
  const raw = typeof value === 'string' ? value.trim() : '';
  return CLOUD_PROVIDERS.some((p) => p.value === raw)
    ? (raw as CloudProviderValue)
    : 'pending';
}

export function buildCloudStorageMeta(input: {
  provider?: unknown;
  folder_label?: unknown;
  folder_id?: unknown;
  connected?: unknown;
}): CloudStorageMeta {
  const provider = normalizeCloudProvider(input.provider);
  const folder_label = String(input.folder_label ?? '').trim();
  const folder_id = String(input.folder_id ?? '').trim() || null;
  const requestedConnected = input.connected === true
    || input.connected === 1
    || input.connected === 'true';

  // Only Google Drive / Workspace can be connected=true, and only with a folder after OAuth.
  const connected = isGoogleCloudProvider(provider) && requestedConnected && Boolean(folder_id);

  return {
    provider,
    folder_label,
    folder_id: isGoogleCloudProvider(provider) ? folder_id : null,
    connected,
    note: CLOUD_STORAGE_NOTE,
  };
}
